import cx from 'classnames';
import type {
  ChangeEventHandler,
  FunctionComponent,
  KeyboardEventHandler,
  MouseEventHandler,
} from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import {
  boardSwitcherSearchSharedState,
  boardSwitcherSharedState,
} from '@trello/board-switcher';
import { useFeatureGate } from '@trello/feature-gate-client';
import { intl } from '@trello/i18n';
import { Key } from '@trello/keybindings';
import { CloseIcon } from '@trello/nachos/icons/close';
import { SearchIcon } from '@trello/nachos/icons/search';
import { Textfield } from '@trello/nachos/textfield';
import { navigate } from '@trello/router/navigate';
import { useSharedStateSelector } from '@trello/shared-state';
import { getTestId, type BoardSwitcherTestIds } from '@trello/test-ids';

import { stopPropagationAndPreventDefault } from 'app/src/stopPropagationAndPreventDefault';
import {
  getFirstNavItem,
  getNavItemElement,
  getNodeData,
} from './getNavItemTreeWalker';
import { keyboardNavSharedState } from './keyboardNavSharedState';
import { SettingsPopoverButton } from './SettingsPopoverButton';
import { ToggleLayoutButton } from './ToggleLayoutButton';
import { useOpenBoardAndHideSwitcher } from './useOpenBoardAndHideSwitcher';

import styles from './SearchField.less';

interface SearchFieldProps {
  isPanel?: boolean;
  showDialogCallback?: () => void;
  hideDialogCallback?: () => void;
}

export const SearchField: FunctionComponent<SearchFieldProps> = ({
  isPanel,
  showDialogCallback,
  hideDialogCallback,
}) => {
  const textfieldRef = useRef<HTMLInputElement>(null);
  const { value: isListViewEnabled } = useFeatureGate(
    'billplat_board_switcher_settings',
  );
  const { value: isBoardSwitcherPanelEnabled } = useFeatureGate(
    'billplat_board_switcher_panel',
  );

  const openBoardAndHideSwitcher = useOpenBoardAndHideSwitcher();
  const switcherMode = useSharedStateSelector(
    boardSwitcherSharedState,
    useCallback((state) => state.mode, []),
  );

  const query = useSharedStateSelector(
    boardSwitcherSearchSharedState,
    useCallback((value) => value.query, []),
  );

  useEffect(() => {
    boardSwitcherSearchSharedState.setValue({
      searchFieldRef: textfieldRef,
    });
  }, []);

  const handleDebouncedKeydownFilter = useDebouncedCallback(() => {
    Analytics.sendUIEvent({
      action: 'keydown',
      actionSubject: 'input',
      actionSubjectId: 'searchFieldInput',
      source: 'boardSwitcherModal',
    });
  }, 500);

  const handleInputClick = useCallback(() => {
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'input',
      actionSubjectId: 'searchFieldInput',
      source: 'boardSwitcherModal',
    });
  }, []);

  const onSearch = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      boardSwitcherSearchSharedState.setValue({
        query: event.target.value,
      });
      keyboardNavSharedState.setValue({
        activeId: null,
      });

      handleDebouncedKeydownFilter();
    },
    [handleDebouncedKeydownFilter],
  );

  const onClear = useCallback<MouseEventHandler>((event) => {
    stopPropagationAndPreventDefault(event);

    Analytics.sendClickedButtonEvent({
      buttonName: 'searchFieldInputClearButton',
      source: 'boardSwitcherModal',
    });

    boardSwitcherSearchSharedState.setValue({
      query: null,
    });
    keyboardNavSharedState.setValue({
      activeId: null,
      activeSection: null,
    });
    textfieldRef.current?.focus();
  }, []);

  const onKeyDown = useCallback<KeyboardEventHandler<HTMLInputElement>>(
    (event) => {
      const input = event.target as HTMLInputElement;

      const isAtEnd = input.selectionStart === input.value.length;

      const isEnter = event.key === Key.Enter;
      const isArrowDown = event.key === Key.ArrowDown;
      const isArrowRight = event.key === Key.ArrowRight;

      if (isEnter || (isAtEnd && (isArrowDown || isArrowRight))) {
        event.preventDefault();
        const newState = getFirstNavItem();

        if (newState) {
          keyboardNavSharedState.setValue({
            activeId: newState.activeId,
            activeSection: newState.activeSection,
          });
          if (isEnter) {
            const element = getNavItemElement(
              newState.activeSection,
              newState.activeId,
            );
            const data = getNodeData(element);

            Analytics.sendClickedLinkEvent({
              linkName: 'boardSwitcherTileLink',
              source: 'boardSwitcherModal',
              attributes: {
                section: 'search',
              },
              containers: formatContainers({
                boardId: data?.id,
              }),
            });

            if (data?.href) {
              navigate(data.href, { trigger: true });
              openBoardAndHideSwitcher();
            }
          }
        }
      }
    },
    [openBoardAndHideSwitcher],
  );

  //unfortunately, we need to do this here instead of using the useAutoFocusRef hook because we need to not
  //autofocus when the board switcher is in panel mode
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isBoardSwitcherPanelEnabled && switcherMode === 'panel') return;

    timeoutRef.current = setTimeout(() => {
      textfieldRef.current?.focus();
      timeoutRef.current = null;
    }, 1);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isBoardSwitcherPanelEnabled, switcherMode, textfieldRef]);

  return (
    <div
      className={cx(styles.container, {
        [styles.panel]: isPanel,
      })}
    >
      <div className={styles.search}>
        <Textfield
          onClick={handleInputClick}
          ref={textfieldRef}
          iconBefore={<SearchIcon />}
          placeholder={intl.formatMessage({
            id: 'templates.board_switcher.search-your-boards',
            defaultMessage: 'Search your boards',
            description: 'Placeholder text on board switcher search field',
          })}
          aria-label={intl.formatMessage({
            id: 'templates.board_switcher.search-your-boards',
            defaultMessage: 'Search your boards',
            description: 'Label text on board switcher search field',
          })}
          autoFocus={true}
          onChange={onSearch}
          onKeyDown={onKeyDown}
          value={query ?? ''}
          className={styles.searchInput}
          testId={getTestId<BoardSwitcherTestIds>(
            'board-switcher-search-field',
          )}
        />
        {query && (
          <button
            onClick={onClear}
            className={styles.clearButton}
            aria-label={intl.formatMessage({
              id: 'templates.board_switcher.clear-boards-search',
              defaultMessage: 'Clear boards search',
              description: 'Aria label on board switcher clear search button',
            })}
          >
            <CloseIcon />
          </button>
        )}
      </div>
      {isListViewEnabled && switcherMode === 'modal' && <ToggleLayoutButton />}
      {isBoardSwitcherPanelEnabled && <SettingsPopoverButton />}
    </div>
  );
};
